import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import axios from '../../../../config/axios'
import plus from '../../../../assets/certificates/AddParameter/blue-plus.svg'
import save from '../../../../assets/certificates/AddParameter/save.svg'
import trash from '../../../../assets/production-flow/trash.svg'
import search from '../../../../assets/production-flow/search.svg'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import {
  MainContainer,
  StyledCard,
  StyledCardHeader,
  FormGrid,
  StyledFormGroup,
  ActionButtons,
  CancelButton,
  SaveButton,
  SubHeaderContainer,
  SectionName,
  LeftSide,
  BreadcrumbsContainer,
  CardTitle,
  AddButton,
  MaterialItemContainer,
  MaterialFields,
  InlineDeleteButton,
  DropdownList,
  DropdownItem,
  SectionButtons,
  OrderButtonsContainer,
  OrderButton,
  ServiceActionsContainer,
} from './style'
import { Textarea } from '../../../../components/styles/modal-style'

interface MaterialItem {
  id: number
  materialId: number
  material: string
  unitOfMeasure: 'KG' | 'MM'
  quantityToUse: string
  isAbated?: boolean
  hasInsufficientStock?: boolean
  fromProduct?: boolean
  originalProductMaterialName?: string
  skipConflictValidation?: boolean
}

interface ResourceItem {
  id: number
  resourceId: number
  resource: string
  unit: string
  quantity: string
  isAbated?: boolean
  fromProduct?: boolean
  skipConflictValidation?: boolean
}

interface ServiceItem {
  id: number
  serviceId: number
  service: string
  description: string
  observation: string
  fromProduct?: boolean
}

interface Order {
  id: number
  client: string
  items: OrderItem[]
  saleNumberPloomes?: string
}

interface OrderItem {
  weight: any
  id: number
  quantity?: number
  material?: { name: string }
  product?: { name: string }
  stage?: { name: string }
  resource?: { name: string }
  stock?: { description: string }
  ploomesProductName?: string
}

interface Product {
  id: number
  name: string
  scripts?: Script[]
}

interface Script {
  id: number
  name: string
  items?: ScriptItem[]
}

interface ScriptItem {
  id: number
  type: 'rawMaterial' | 'service' | 'resource' | 'productFinal'
  order: number
  rawMaterial?: { id: number; name: string }
  rawMaterialId?: number
  rawMaterialName?: string
  service?: { id: number; name: string }
  serviceId?: number
  serviceName?: string
  resource?: { id: number; name: string }
  resourceId?: number
  resourceName?: string
  productFinal?: { id: number; name: string }
  productFinalId?: number
  productFinalName?: string
  weight?: number
  quantity?: number
  executionTime?: string
  requiredQualification?: string
  deliveryTime?: number
  company?: { id: number; name: string }
  companyId?: number
  companyName?: string
  unitOfMeasurementId?: number
  unitOfMeasurement?: { id: number; name: string }
  idealMeasurements?: string
  description?: string | null
  observation?: string | null
}

interface FormState {
  codeOfOP: string
  orderNumber: string
  orderItemId: number
  item: string
  order: string
  quantity: string
  weight: string
  client: string
  materials: MaterialItem[]
  resources: ResourceItem[]
  services: ServiceItem[]
}

interface SavedStageData {
  id: number
  serviceId: number
  stageId: number
  completed: boolean
  index: number
  description?: string
  observation?: string
  stage: {
    name: string
    id: number
  }
}

const INITIAL_FORM_STATE: FormState = {
  codeOfOP: '',
  orderNumber: '',
  orderItemId: 0,
  item: '',
  order: '',
  quantity: '',
  weight: '',
  client: '',
  materials: [],
  resources: [],
  services: [],
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({
  items,
}) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const getToken = () => localStorage.getItem('token')

const CreateOP = () => {
  const navigate = useNavigate()
  const { id: opId } = useParams<{ id: string }>()
  const isEditMode = Boolean(opId)
  const { setFeedback } = useContext(FeedbackContext)

  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE)
  const [searchMaterial, setSearchMaterial] = useState('')
  const [fixedMaterialFilters, setFixedMaterialFilters] = useState<
    Record<number, string>
  >({})
  const [searchResource, setSearchResource] = useState('')
  const [searchService, setSearchService] = useState('')
  const [searchOrder, setSearchOrder] = useState('')
  const [searchProduct, setSearchProduct] = useState('')
  const [orderOptions, setOrderOptions] = useState<Order[]>([])
  const [productOptions, setProductOptions] = useState<Product[]>([])
  const [scriptOptions, setScriptOptions] = useState<Script[]>([])
  const [materialOptions, setMaterialOptions] = useState<
    { id: number; name: string }[]
  >([])
  const [resourceOptions, setResourceOptions] = useState<
    { id: number; name: string }[]
  >([])
  const [serviceOptions, setServiceOptions] = useState<
    { id: number; name: string }[]
  >([])
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<
    number | null
  >(null)
  const [editingResourceIndex, setEditingResourceIndex] = useState<
    number | null
  >(null)
  const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(
    null,
  )
  const [isEditingOrder, setIsEditingOrder] = useState(false)
  const [isEditingProduct, setIsEditingProduct] = useState(false)
  const [isEditingScript, setIsEditingScript] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<{
    id: number
    name: string
  } | null>(null)
  const [selectedScript, setSelectedScript] = useState<{
    id: number
    name: string
  } | null>(null)

  const hasMaterials = form.materials.length > 0
  const hasAbatedMaterials = form.materials.some((m) => m.isAbated)
  const hasPendingMaterials = form.materials.some(
    (m) => !m.isAbated && !m.fromProduct && !m.skipConflictValidation,
  )
  const hasResources = form.resources.length > 0
  const hasAbatedResources = form.resources.some((r) => r.isAbated)
  const hasPendingResources = form.resources.some(
    (r) => !r.isAbated && !r.fromProduct && !r.skipConflictValidation,
  )

  const hasAbatedItems = hasAbatedMaterials || hasAbatedResources

  const canAddMaterial = !hasAbatedMaterials && !hasPendingResources
  const canAddResource = !hasAbatedResources && !hasPendingMaterials

  const materialMessage = hasAbatedMaterials
    ? 'Já abatidos'
    : hasPendingResources
    ? 'Remova insumos pendentes'
    : ''
  const resourceMessage = hasAbatedResources
    ? 'Já abatidos'
    : hasPendingMaterials
    ? 'Remova materiais pendentes'
    : ''

  const createEmptyMaterial = (): MaterialItem => ({
    id: Date.now(),
    materialId: 0,
    material: '',
    unitOfMeasure: 'MM',
    quantityToUse: '',
    hasInsufficientStock: false,
  })

  const createEmptyResource = (): ResourceItem => ({
    id: Date.now(),
    resourceId: 0,
    resource: '',
    unit: 'KG',
    quantity: '',
  })

  const createEmptyService = (): ServiceItem => ({
    id: Date.now(),
    serviceId: 0,
    service: '',
    description: '',
    observation: '',
  })

  const getItemDescription = (orderItem: OrderItem): string => {
    return orderItem.ploomesProductName || 'Item sem descrição'
  }

  const getClientName = (client: any): string => {
    if (!client) return ''
    if (typeof client === 'object')
      return client.companyName || client.name || ''
    if (typeof client === 'string') return client
    return ''
  }

  const filterOrders = useCallback((orders: Order[], search: string) => {
    if (!search) return orders

    const searchLower = String(search).toLowerCase().trim()

    return orders.filter((order: any) => {
      const saleNumber = String(order.saleNumberPloomes || '').toLowerCase()
      return saleNumber.includes(searchLower)
    })
  }, [])

  const processMaterials = useCallback((data: any): MaterialItem[] => {
    const list = data.StockInProduction || data.stockInProduction || []
    if (!list.length) return []

    return list
      .filter((stockItem: any) => stockItem.stockId !== null)
      .map((stockItem: any) => {
        const stock = stockItem.Stock || stockItem.stock || {}
        return {
          id: stockItem.id || Date.now() + Math.random(),
          materialId: stock.id || stockItem.stockId || 0,
          material:
            stock.description || stock.name || `Material ID: ${stock.id}`,
          unitOfMeasure: stockItem.usedUnitOfMeasurement || 'KG',
          quantityToUse: stockItem.usedQuantity?.toString() || '',
          isAbated: true,
          hasInsufficientStock: false,
        }
      })
  }, [])

  const processServices = useCallback((serviceStages: any[]): ServiceItem[] => {
    if (!serviceStages?.length) return []

    return serviceStages.map((stage: SavedStageData) => ({
      id: stage.id || Date.now() + Math.random(),
      serviceId: stage.stageId,
      service: (stage as any).Stage?.name || stage.stage?.name || '',
      description: stage.description || '',
      observation: stage.observation || '',
    }))
  }, [])

  const processResources = useCallback((data: any): ResourceItem[] => {
    const list = data.StockInProduction || data.stockInProduction || []
    if (list.length > 0) {
      const resourceItems = list.filter(
        (stockItem: any) => stockItem.resourceId !== null,
      )
      if (resourceItems.length > 0) {
        return resourceItems.map((stockItem: any) => {
          const resource = stockItem.Resource || stockItem.resource || {}
          return {
            id: stockItem.id || Date.now() + Math.random(),
            resourceId: resource.id || stockItem.resourceId || 0,
            resource:
              resource.name ||
              resource.description ||
              `Insumo ID: ${resource.id}`,
            unit: stockItem.usedUnitOfMeasurement || 'KG',
            quantity: stockItem.usedQuantity?.toString() || '',
            isAbated: true,
          }
        })
      }
    }

    if (data.orderItem?.resource) {
      const resource = data.orderItem.resource
      return [
        {
          id: resource.id || Date.now() + Math.random(),
          resourceId: resource.id,
          resource: resource.name || resource.description || '',
          unit: resource.stockUnit || 'KG',
          quantity: '',
          isAbated: false,
        },
      ]
    }

    return []
  }, [])

  const validateStockFormat = (
    stock: any,
    unitOfMeasure: string,
  ): { isValid: boolean; message?: string } => {
    const formatType = stock.format?.type?.toLowerCase() || 'indefinido'
    const dim1 = stock.dimension1 || 0
    const dim2 = stock.dimension2 || 0
    const lengthInMm = stock.lengthInMm || 0
    const totalWeight = stock.totalWeight || 0
    const density = stock.material?.density || 0

    const missingFields: string[] = []

    if (unitOfMeasure === 'KG') {
      if (totalWeight <= 0) missingFields.push('peso')
    } else if (unitOfMeasure === 'MM') {
      if (lengthInMm <= 0) missingFields.push('comprimento')
    }

    if (density <= 0) missingFields.push('densidade')

    switch (formatType) {
      case 'redondo':
      case 'sextavado':
        if (dim1 <= 0) missingFields.push('dimensão 1')
        break
      case 'quadrado':
      case 'chapa':
      case 'tubo':
      case 'anel':
        if (dim1 <= 0) missingFields.push('dimensão 1')
        if (dim2 <= 0) missingFields.push('dimensão 2')
        break
      case 'indefinido':
        break
      default:
        return {
          isValid: false,
          message: `Formato de material desconhecido: ${formatType}`,
        }
    }

    if (missingFields.length > 0) {
      return {
        isValid: false,
        message: `Material do tipo ${formatType} sem os seguintes campos: ${missingFields.join(
          ', ',
        )}`,
      }
    }

    return { isValid: true }
  }

  const checkMaterialAvailability = async (
    materialId: number,
    quantityToUse: number,
    unitOfMeasure: string,
  ): Promise<{ available: boolean; message?: string }> => {
    try {
      const token = getToken()
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/raw-materials/${materialId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )

      const stock = response.data
      if (!stock)
        return {
          available: false,
          message: 'Material não encontrado no estoque',
        }

      const formatValidation = validateStockFormat(stock, unitOfMeasure)
      if (!formatValidation.isValid)
        return { available: false, message: formatValidation.message }

      const availableLengthInMm = stock.lengthInMm || 0
      const availableWeight = stock.totalWeight || 0

      if (unitOfMeasure === 'KG') {
        if (quantityToUse > availableWeight) {
          return {
            available: false,
            message: `Quantidade insuficiente. Disponível: ${availableWeight.toFixed(
              2,
            )} KG`,
          }
        }
      } else if (unitOfMeasure === 'MM') {
        if (quantityToUse > availableLengthInMm) {
          return {
            available: false,
            message: `Comprimento insuficiente. Disponível: ${availableLengthInMm.toFixed(
              2,
            )} MM`,
          }
        }
      }

      return { available: true }
    } catch (error) {
      return { available: true }
    }
  }

  const loadScriptItems = useCallback(
    async (scriptId: number) => {
      try {
        const token = getToken()
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/product-final/script/${scriptId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )

        const script = response.data
        if (!script || !script.items || script.items.length === 0) {
          setFeedback({
            isOpen: true,
            statusOk: false,
            message: 'Script não possui itens',
          })
          return
        }

        const newMaterials: MaterialItem[] = []
        const newResources: ResourceItem[] = []
        const newServices: ServiceItem[] = []

        const loadProductFinalItems = async (productFinalId: number) => {
          try {
            const productResponse = await axios.get(
              `${process.env.REACT_APP_GATEWAY_BASE_URL}/product-final/product/${productFinalId}`,
              { headers: { Authorization: `Bearer ${token}` } },
            )

            const product = productResponse.data

            if (product.scripts && product.scripts.length > 0) {
              const productScript = product.scripts[0]

              const scriptResponse = await axios.get(
                `${process.env.REACT_APP_GATEWAY_BASE_URL}/product-final/script/${productScript.id}`,
                { headers: { Authorization: `Bearer ${token}` } },
              )

              return scriptResponse.data.items || []
            }

            return []
          } catch (error) {
            console.error(
              `Erro ao carregar itens do produto ${productFinalId}:`,
              error,
            )
            return []
          }
        }

        for (const item of script.items) {
          if (item.type === 'rawMaterial' && item.rawMaterial) {
            const materialName =
              item.rawMaterial.name || item.rawMaterialName || ''
            newMaterials.push({
              id: Date.now() + Math.random(),
              materialId: item.rawMaterial.id || item.rawMaterialId || 0,
              material: materialName,
              unitOfMeasure: 'KG',
              quantityToUse: item.weight?.toString() || '',
              isAbated: false,
              hasInsufficientStock: false,
              fromProduct: true,
              originalProductMaterialName: materialName,
              skipConflictValidation: true,
            })
          } else if (item.type === 'resource' && item.resource) {
            const unitName = item.unitOfMeasurement?.name || 'KG'
            newResources.push({
              id: Date.now() + Math.random(),
              resourceId: item.resource.id || item.resourceId || 0,
              resource: item.resource.name || item.resourceName || '',
              unit: unitName,
              quantity:
                item.weight?.toString() || item.quantity?.toString() || '',
              isAbated: false,
              fromProduct: true,
              skipConflictValidation: true,
            })
          } else if (item.type === 'service' && item.service) {
            newServices.push({
              id: Date.now() + Math.random(),
              serviceId: item.service.id || item.serviceId || 0,
              service: item.service.name || item.serviceName || '',
              description: item.description || '',
              observation: item.observation || '',
              fromProduct: true,
            })
          } else if (
            item.type === 'productFinal' &&
            (item.productFinal || item.productFinalId)
          ) {
            const productFinalId = item.productFinal?.id || item.productFinalId
            if (productFinalId) {
              const productItems = await loadProductFinalItems(productFinalId)

              for (const subItem of productItems) {
                if (subItem.type === 'rawMaterial' && subItem.rawMaterial) {
                  const materialName =
                    subItem.rawMaterial.name || subItem.rawMaterialName || ''
                  newMaterials.push({
                    id: Date.now() + Math.random(),
                    materialId:
                      subItem.rawMaterial.id || subItem.rawMaterialId || 0,
                    material: materialName,
                    unitOfMeasure: 'KG',
                    quantityToUse: subItem.weight?.toString() || '',
                    isAbated: false,
                    hasInsufficientStock: false,
                    fromProduct: true,
                    originalProductMaterialName: materialName,
                    skipConflictValidation: true,
                  })
                } else if (subItem.type === 'resource' && subItem.resource) {
                  const unitName = subItem.unitOfMeasurement?.name || 'KG'
                  newResources.push({
                    id: Date.now() + Math.random(),
                    resourceId: subItem.resource.id || subItem.resourceId || 0,
                    resource:
                      subItem.resource.name || subItem.resourceName || '',
                    unit: unitName,
                    quantity:
                      subItem.weight?.toString() ||
                      subItem.quantity?.toString() ||
                      '',
                    isAbated: false,
                    fromProduct: true,
                    skipConflictValidation: true,
                  })
                } else if (subItem.type === 'service' && subItem.service) {
                  newServices.push({
                    id: Date.now() + Math.random(),
                    serviceId: subItem.service.id || subItem.serviceId || 0,
                    service: subItem.service.name || subItem.serviceName || '',
                    description: subItem.description || '',
                    observation: subItem.observation || '',
                    fromProduct: true,
                  })
                }
              }
            }
          }
        }

        setForm((prev) => ({
          ...prev,
          materials: [...prev.materials, ...newMaterials],
          resources: [...prev.resources, ...newResources],
          services: [...prev.services, ...newServices],
        }))

        setFeedback({
          isOpen: true,
          statusOk: true,
          message: `${newMaterials.length} material(is), ${newResources.length} insumo(s) e ${newServices.length} serviço(s) carregados do script.`,
        })
      } catch (error) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Erro ao carregar itens do script.',
        })
      }
    },
    [setFeedback],
  )

  const fetchOPData = useCallback(async () => {
    if (!isEditMode || !opId) return

    setIsLoading(true)
    try {
      const token = getToken()
      const [basicResponse, serviceStagesResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${opId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/services-of-op/${opId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ])

      const processedMaterials = processMaterials(basicResponse.data)
      const processedResources = processResources(basicResponse.data)

      const serviceStages = serviceStagesResponse.data || []
      const processedServices = processServices(serviceStages)

      let orderItem = basicResponse.data.orderItem
      let order = orderItem?.order

      if (!orderItem && basicResponse.data.orderItemId) {
        try {
          const ordersResponse = await axios.get(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/order`,
            { headers: { Authorization: `Bearer ${token}` } },
          )
          const orders = Array.isArray(ordersResponse.data)
            ? ordersResponse.data
            : ordersResponse.data?.data || []

          for (const o of orders) {
            const found = (o.items || o.OrderItem || []).find(
              (i: any) => i.id === basicResponse.data.orderItemId,
            )
            if (found) {
              orderItem = found
              order = o
              break
            }
          }
        } catch (error) {
          //
        }
      }

      setForm({
        codeOfOP: basicResponse.data.codeOfOP,
        orderNumber: `OP-${opId}`,
        orderItemId: basicResponse.data.orderItemId || 0,
        item: orderItem ? getItemDescription(orderItem) : 'Item do pedido',
        order: (order as any)?.saleNumberPloomes || order?.id?.toString() || '',
        quantity:
          orderItem?.quantity?.toString() ||
          orderItem?.quantityToProduce?.toString() ||
          '',
        weight: orderItem?.weight?.toString() || '',
        client: getClientName(
          order?.client || order?.Client || basicResponse.data.client,
        ),
        materials: processedMaterials,
        resources: processedResources,
        services: processedServices,
      })
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao carregar dados da OP',
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    isEditMode,
    opId,
    processMaterials,
    processResources,
    processServices,
    setFeedback,
  ])

  useEffect(() => {
    fetchOPData()
  }, [fetchOPData])

  const fetchData = useCallback(
    async (url: string, setter: any, search?: string) => {
      try {
        const token = getToken()
        const response = await axios.get(url, {
          params: search ? { search } : {},
          headers: { Authorization: `Bearer ${token}` },
        })

        if (response.data) {
          if (Array.isArray(response.data)) {
            setter(response.data)
          } else if (response.data.data && Array.isArray(response.data.data)) {
            setter(response.data.data)
          } else {
            setter([])
          }
        } else {
          setter([])
        }
      } catch (error) {
        setter([])
      }
    },
    [],
  )

  const fetchOrders = useCallback(async (search?: string) => {
    try {
      const token = getToken()
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/order`,
        {
          params: search ? { search } : {},
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      let orders = []
      if (Array.isArray(response.data)) {
        orders = response.data
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        orders = response.data.data
      }

      setOrderOptions(orders)
    } catch (error) {
      setOrderOptions([])
    }
  }, [])

  useEffect(() => {
    if (isEditingOrder) {
      const timeoutId = setTimeout(() => {
        fetchOrders(searchOrder)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [isEditingOrder, searchOrder, fetchOrders])

  const fetchProducts = useCallback(async (search?: string) => {
    try {
      const token = getToken()
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/product-final/product`,
        {
          params: { limit: 100, ...(search ? { search } : {}) },
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      let products = []
      if (Array.isArray(response.data)) {
        products = response.data
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        products = response.data.data
      }

      setProductOptions(products)
    } catch (error) {
      setProductOptions([])
    }
  }, [])

  useEffect(() => {
    if (isEditingProduct) {
      const timeoutId = setTimeout(() => {
        fetchProducts(searchProduct)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [isEditingProduct, searchProduct, fetchProducts])

  useEffect(() => {
    if (editingMaterialIndex !== null) {
      const timeoutId = setTimeout(
        () => {
          const filterToUse =
            fixedMaterialFilters[editingMaterialIndex] || searchMaterial
          fetchData(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/raw-materials`,
            setMaterialOptions,
            filterToUse,
          )
        },
        searchMaterial ? 300 : 0,
      )
      return () => clearTimeout(timeoutId)
    }
  }, [editingMaterialIndex, searchMaterial, fixedMaterialFilters, fetchData])

  useEffect(() => {
    if (editingResourceIndex !== null) {
      const timeoutId = setTimeout(
        () => {
          fetchData(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/resources`,
            setResourceOptions,
            searchResource,
          )
        },
        searchResource ? 300 : 0,
      )
      return () => clearTimeout(timeoutId)
    }
  }, [editingResourceIndex, searchResource, fetchData])

  useEffect(() => {
    if (editingServiceIndex !== null) {
      const timeoutId = setTimeout(
        () => {
          fetchData(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/stages`,
            setServiceOptions,
            searchService,
          )
        },
        searchService ? 300 : 0,
      )
      return () => clearTimeout(timeoutId)
    }
  }, [editingServiceIndex, searchService, fetchData])

  const selectOrder = useCallback((order: Order, orderItem: OrderItem) => {
    setForm((prev) => ({
      ...prev,
      order: (order as any).saleNumberPloomes || order.id.toString(),
      orderItemId: orderItem.id,
      item: getItemDescription(orderItem),
      client: order.client,
      weight: orderItem?.weight?.toString() || '',
    }))
    setIsEditingOrder(false)
    setSearchOrder('')
  }, [])

  const selectProduct = useCallback(
    (product: Product) => {
      const hasAbatedItems =
        form.materials.some((m) => m.isAbated) ||
        form.resources.some((r) => r.isAbated)

      if (hasAbatedItems) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Reverta a OP antes de adicionar mais itens.',
        })
        return
      }

      setSelectedProduct({ id: product.id, name: product.name })
      setSelectedScript(null)
      setScriptOptions(product.scripts || [])
      setIsEditingProduct(false)
      setSearchProduct('')
    },
    [form.materials, form.resources, setFeedback],
  )

  const selectScript = useCallback(
    (script: Script) => {
      const hasAbatedItems =
        form.materials.some((m) => m.isAbated) ||
        form.resources.some((r) => r.isAbated)

      if (hasAbatedItems) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Reverta a OP antes de adicionar mais itens.',
        })
        return
      }

      setForm((prev) => ({
        ...prev,
        materials: prev.materials.filter((m) => m.isAbated),
        resources: prev.resources.filter((r) => r.isAbated),
        services: [],
      }))
      setSelectedScript({ id: script.id, name: script.name })
      setIsEditingScript(false)
      loadScriptItems(script.id)
    },
    [form.materials, form.resources, loadScriptItems, setFeedback],
  )

  const addMaterial = useCallback(() => {
    if (!canAddMaterial) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: materialMessage || 'Não é possível adicionar material',
      })
      return
    }
    setForm((prev) => ({
      ...prev,
      materials: [...prev.materials, createEmptyMaterial()],
    }))
    setEditingMaterialIndex(form.materials.length)
    setSearchMaterial('')
  }, [canAddMaterial, materialMessage, form.materials.length, setFeedback])

  const addResource = useCallback(() => {
    if (!canAddResource) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: resourceMessage || 'Não é possível adicionar insumo',
      })
      return
    }
    setForm((prev) => ({
      ...prev,
      resources: [...prev.resources, createEmptyResource()],
    }))
    setEditingResourceIndex(form.resources.length)
    setSearchResource('')
  }, [canAddResource, resourceMessage, form.resources.length, setFeedback])

  const addService = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      services: [...prev.services, createEmptyService()],
    }))
    setEditingServiceIndex(form.services.length)
    setSearchService('')
  }, [form.services.length])

  const updateItem = useCallback(
    async (
      section: 'materials' | 'resources' | 'services',
      id: number,
      field: string,
      value: any,
    ) => {
      setForm((prev) => ({
        ...prev,
        [section]: prev[section].map((item) =>
          item.id === id
            ? {
                ...item,
                [field]: value,
                fromProduct: item.fromProduct,
                ...('skipConflictValidation' in item && {
                  skipConflictValidation: (item as MaterialItem | ResourceItem)
                    .skipConflictValidation,
                }),
              }
            : item,
        ),
      }))

      if (
        section === 'materials' &&
        (field === 'quantityToUse' || field === 'unitOfMeasure')
      ) {
        const material = form.materials.find((m) => m.id === id)

        if (material && material.materialId > 0) {
          const quantity =
            field === 'quantityToUse'
              ? parseFloat(value.replace(',', '.'))
              : parseFloat(material.quantityToUse.replace(',', '.'))
          const unit =
            field === 'unitOfMeasure' ? value : material.unitOfMeasure

          if (quantity > 0 && !isNaN(quantity)) {
            const availability = await checkMaterialAvailability(
              material.materialId,
              quantity,
              unit,
            )

            setForm((prev) => ({
              ...prev,
              materials: prev.materials.map((m) =>
                m.id === id
                  ? {
                      ...m,
                      hasInsufficientStock: !availability.available,
                      fromProduct: m.fromProduct,
                      skipConflictValidation: m.skipConflictValidation,
                    }
                  : m,
              ),
            }))

            if (!availability.available) {
              setFeedback({
                isOpen: true,
                statusOk: false,
                message:
                  availability.message || 'Quantidade insuficiente no estoque',
              })
            }
          }
        }
      }
    },
    [form.materials, setFeedback],
  )

  const selectItem = useCallback(
    async (
      section: 'materials' | 'resources' | 'services',
      index: number,
      option: { id: number; name: string; description?: string; observation?: string },
    ) => {
      const fieldMap = {
        materials: { idField: 'materialId', nameField: 'material' },
        resources: { idField: 'resourceId', nameField: 'resource' },
        services: { idField: 'serviceId', nameField: 'service' },
      }

      const { idField, nameField } = fieldMap[section]

      if (section === 'materials') {
        const material = form.materials[index]
        if (material.quantityToUse) {
          const quantity = parseFloat(material.quantityToUse.replace(',', '.'))
          if (quantity > 0 && !isNaN(quantity)) {
            const availability = await checkMaterialAvailability(
              option.id,
              quantity,
              material.unitOfMeasure,
            )

            setForm((prev) => ({
              ...prev,
              [section]: prev[section].map((item, i) => {
                if (i !== index) return item
                return {
                  ...item,
                  [idField]: option.id,
                  [nameField]: option.name,
                  hasInsufficientStock: !availability.available,
                  fromProduct: item.fromProduct,
                  skipConflictValidation: item.skipConflictValidation,
                  originalProductMaterialName: item.originalProductMaterialName,
                }
              }),
            }))

            if (!availability.available) {
              setFeedback({
                isOpen: true,
                statusOk: false,
                message:
                  availability.message || 'Quantidade insuficiente no estoque',
              })
            }

            setEditingMaterialIndex(null)
            setSearchMaterial('')
            return
          }
        }
      }

      setForm((prev) => ({
        ...prev,
        [section]: prev[section].map((item, i) => {
          if (i !== index) return item
          return {
            ...item,
            [idField]: option.id,
            [nameField]: option.name,
            fromProduct: item.fromProduct,
            ...('skipConflictValidation' in item && {
              skipConflictValidation: (item as MaterialItem | ResourceItem)
                .skipConflictValidation,
            }),
          }
        }),
      }))

      setEditingMaterialIndex(null)
      setEditingResourceIndex(null)
      setEditingServiceIndex(null)
      setSearchMaterial('')
      setSearchResource('')
      setSearchService('')

      if (section === 'materials') {
        setFixedMaterialFilters((prev) => {
          const newFilters = { ...prev }
          delete newFilters[index]
          return newFilters
        })
      }
    },
    [form.materials, setFeedback],
  )

  const removeItem = useCallback(
    (section: 'materials' | 'resources' | 'services', id: number) => {
      setForm((prev) => ({
        ...prev,
        [section]: prev[section].filter((item) => item.id !== id),
      }))
    },
    [],
  )

  const reorderService = useCallback(
    (index: number, direction: 'up' | 'down') => {
      setForm((prev) => {
        const services = [...prev.services]
        const newIndex = direction === 'up' ? index - 1 : index + 1
        if (newIndex < 0 || newIndex >= services.length) return prev
        ;[services[index], services[newIndex]] = [
          services[newIndex],
          services[index],
        ]
        return { ...prev, services }
      })
    },
    [],
  )

  const validateForm = useCallback(async (): Promise<string | null> => {
    if (!form.orderItemId) return 'Selecione um pedido e um item.'

    if (
      form.services.some(
        (srv) => srv.service && (!srv.serviceId || srv.serviceId === 0),
      )
    ) {
      return 'Se adicionar serviços, todos devem ter um serviço selecionado.'
    }

    const pendingMaterials = form.materials.filter(
      (m) => !m.isAbated && !m.fromProduct && !m.skipConflictValidation,
    )
    const pendingResources = form.resources.filter(
      (r) => !r.isAbated && !r.fromProduct && !r.skipConflictValidation,
    )

    if (pendingMaterials.length > 0 && pendingResources.length > 0) {
      return 'Não é possível ter materiais e insumos pendentes simultaneamente. Remova um dos tipos.'
    }

    if (
      form.materials.length > 0 &&
      form.materials.some(
        (mat) =>
          !mat.materialId ||
          mat.materialId === 0 ||
          !mat.quantityToUse ||
          parseFloat(mat.quantityToUse.replace(',', '.')) <= 0,
      )
    ) {
      return 'Se adicionar materiais, todos devem ter um material selecionado e quantidade válida.'
    }

    if (pendingMaterials.length > 0) {
      const allSelected = pendingMaterials.every((m) => m.materialId > 0)
      if (!allSelected)
        return 'Todos os materiais devem ter um material selecionado antes de salvar.'

      for (const material of pendingMaterials) {
        const quantity = parseFloat(material.quantityToUse.replace(',', '.'))
        if (quantity > 0 && !isNaN(quantity)) {
          const availability = await checkMaterialAvailability(
            material.materialId,
            quantity,
            material.unitOfMeasure,
          )
          if (!availability.available)
            return (
              availability.message ||
              'Quantidade insuficiente em um ou mais materiais.'
            )
        }
      }
    }

    if (
      form.resources.length > 0 &&
      form.resources.some(
        (res) =>
          !res.resourceId ||
          res.resourceId === 0 ||
          !res.quantity ||
          parseFloat(res.quantity.replace(',', '.')) <= 0,
      )
    ) {
      return 'Se adicionar insumos, todos devem ter um insumo selecionado e quantidade válida.'
    }

    if (pendingResources.length > 0) {
      const allSelected = pendingResources.every((r) => r.resourceId > 0)
      if (!allSelected)
        return 'Todos os insumos devem ter um insumo selecionado antes de salvar.'
    }

    const resourcesWithInvalidUnit = form.resources.filter(
      (res) =>
        res.unit === 'UN' &&
        !Number.isInteger(parseFloat(res.quantity.replace(',', '.'))),
    )
    if (resourcesWithInvalidUnit.length > 0)
      return 'Insumos com unidade "UN" devem ter quantidade em números inteiros.'

    return null
  }, [form])

  const discountItemsAutomatically = async (
    opId: number,
    materials: MaterialItem[],
    resources: ResourceItem[],
  ) => {
    try {
      const token = getToken()
      const itemsToDiscount: any = {}

      if (materials.length > 0) {
        itemsToDiscount.materials = materials.map((item, index) => ({
          id: index + 1,
          material: { id: item.materialId, name: item.material },
          quantityToUse: parseFloat(item.quantityToUse.replace(',', '.')),
          unitOfMeasure: item.unitOfMeasure,
        }))
      }

      if (resources.length > 0) {
        itemsToDiscount.resources = resources.map((item, index) => ({
          id: index + 1,
          material: { id: item.resourceId, name: item.resource },
          quantityToUse: parseFloat(item.quantity.replace(',', '.')),
          unitOfMeasure: item.unit,
        }))
      }

      await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/discount/${opId}`,
        itemsToDiscount,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          timeout: 30000,
        },
      )

      return { success: true }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Erro desconhecido no abatimento'
      return { success: false, error: errorMessage }
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()

    const validationError = await validateForm()
    if (validationError) {
      setFeedback({ isOpen: true, statusOk: false, message: validationError })
      return
    }

    if (!getToken()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Sessão expirada. Faça login novamente.',
      })
      return
    }

    setIsSubmitting(true)

    try {
      const token = getToken()
      const dataToSend: any = {
        orderItemId: parseInt(form.orderItemId.toString(), 10),
        quantity: form.quantity ? parseInt(form.quantity, 10) : null,
        stages: form.services.map((service, index) => ({
          stageId: parseInt(service.serviceId.toString(), 10),
          index: index,
          description: service.description || '',
          observation: service.observation || '',
        })),
      }

      let response, createdOpId

      if (isEditMode && opId) {
        response = await axios.patch(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${opId}`,
          dataToSend,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        createdOpId = parseInt(opId)
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service`,
          dataToSend,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        createdOpId =
          response.data?.id ||
          (typeof response.data === 'string'
            ? parseInt(response.data.match(/OP[\s](\d+)/)?.[1] || '0')
            : 0)
      }

      let abatimentoMessage = ''

      const materialsToAbate = form.materials.filter((m) => !m.isAbated)
      const resourcesToAbate = form.resources.filter((r) => !r.isAbated)

      const productMaterials = materialsToAbate.filter(
        (m) => m.skipConflictValidation,
      )
      const productResources = resourcesToAbate.filter(
        (r) => r.skipConflictValidation,
      )
      const manualMaterials = materialsToAbate.filter(
        (m) => !m.skipConflictValidation,
      )
      const manualResources = resourcesToAbate.filter(
        (r) => !r.skipConflictValidation,
      )

      if (createdOpId) {
        const abatimentoResults = []

        if (productMaterials.length > 0) {
          const result = await discountItemsAutomatically(
            createdOpId,
            productMaterials,
            [],
          )
          abatimentoResults.push(
            result.success
              ? `${productMaterials.length} material(is) do produto abatidos`
              : `Falha ao abater materiais do produto: ${result.error}`,
          )
        }

        if (productResources.length > 0) {
          const result = await discountItemsAutomatically(
            createdOpId,
            [],
            productResources,
          )
          abatimentoResults.push(
            result.success
              ? `${productResources.length} insumo(s) do produto abatidos`
              : `Falha ao abater insumos do produto: ${result.error}`,
          )
        }

        if (manualMaterials.length > 0 || manualResources.length > 0) {
          const result = await discountItemsAutomatically(
            createdOpId,
            manualMaterials,
            manualResources,
          )
          if (result.success) {
            const messages = []
            if (manualMaterials.length > 0)
              messages.push(`${manualMaterials.length} material(is) manual(is)`)
            if (manualResources.length > 0)
              messages.push(`${manualResources.length} insumo(s) manual(is)`)
            abatimentoResults.push(`${messages.join(' e ')} abatidos`)
          } else {
            abatimentoResults.push(
              `Falha ao abater itens manuais: ${result.error}`,
            )
          }
        }

        if (abatimentoResults.length > 0)
          abatimentoMessage = ` ${abatimentoResults.join('. ')}.`
      }

      setFeedback({
        isOpen: true,
        statusOk: true,
        message: `OP ${
          isEditMode ? 'atualizada' : 'criada'
        } com sucesso!${abatimentoMessage}`,
      })
      navigate('../')
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Erro desconhecido'
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: `Erro ao ${
          isEditMode ? 'atualizar' : 'criar'
        } ordem de produção: ${errorMessage}`,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = useCallback(() => {
    navigate('/panel/production')
  }, [navigate])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.dropdown-container')) {
        setEditingMaterialIndex(null)
        setEditingResourceIndex(null)
        setEditingServiceIndex(null)
        setIsEditingOrder(false)
        setIsEditingProduct(false)
        setIsEditingScript(false)
        setFixedMaterialFilters({})
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const breadcrumbItems = [
    { label: 'Ordem de produção', path: '/panel/production' },
    {
      label: isEditMode
        ? 'Editar ordem de produção'
        : 'Criar ordem de produção',
    },
  ]

  if (isLoading) {
    return (
      <MainContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <p>Carregando dados da OP {opId}...</p>
        </div>
      </MainContainer>
    )
  }

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>
            {isEditMode && form.orderNumber
              ? `Ordem de produção: ${form.codeOfOP}`
              : 'Criar ordem de produção'}
          </SectionName>
        </LeftSide>
        <ActionButtons>
          <CancelButton type='button' onClick={handleCancel}>
            Cancelar
          </CancelButton>
          <SaveButton
            type='button'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <img src={save} alt='Salvar' />
            {isSubmitting
              ? isEditMode
                ? 'Atualizando...'
                : 'Criando OP...'
              : isEditMode
              ? 'Atualizar OP'
              : 'Salvar OP'}
          </SaveButton>
        </ActionButtons>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <MainContainer>
        <form onSubmit={handleSubmit}>
          <StyledCard>
            <CardTitle>Geral</CardTitle>
            <FormGrid cols={2}>
              <StyledFormGroup className='dropdown-container'>
                <label htmlFor='order'>Pedido</label>
                <input
                  type='text'
                  id='order'
                  value={isEditingOrder ? searchOrder : form.order}
                  onChange={(e) => {
                    setSearchOrder(e.target.value)
                    setIsEditingOrder(true)
                  }}
                  onFocus={() => {
                    setIsEditingOrder(true)
                    setSearchOrder(form.order)
                  }}
                  disabled={isEditMode}
                />
                {isEditingOrder && (
                  <DropdownList>
                    {filterOrders(orderOptions, searchOrder).map(
                      (order: any) => (
                        <DropdownItem
                          key={order.id}
                          onClick={() =>
                            order.items?.length > 0 &&
                            selectOrder(order, order.items[0])
                          }
                        >
                          {order.saleNumberPloomes}
                        </DropdownItem>
                      ),
                    )}
                    {filterOrders(orderOptions, searchOrder).length === 0 && (
                      <DropdownItem
                        style={{ pointerEvents: 'none', opacity: 0.6 }}
                      >
                        Nenhum pedido encontrado
                      </DropdownItem>
                    )}
                  </DropdownList>
                )}
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='client'>Cliente</label>
                <input
                  type='text'
                  id='client'
                  value={form.client}
                  readOnly
                  disabled
                />
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='item'>Item</label>
                <input
                  type='text'
                  id='item'
                  value={form.item}
                  readOnly
                  disabled
                />
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='weight'>Peso</label>
                <input
                  type='number'
                  id='weight'
                  value={form.weight}
                  readOnly
                  disabled
                />
              </StyledFormGroup>
            </FormGrid>
          </StyledCard>

          <StyledCard>
            <CardTitle>Produto</CardTitle>
            <FormGrid cols={2}>
              <StyledFormGroup className='dropdown-container'>
                <label htmlFor='product'>Produto</label>
                <input
                  type='text'
                  id='product'
                  value={
                    isEditingProduct
                      ? searchProduct
                      : selectedProduct?.name || ''
                  }
                  onChange={(e) => {
                    const value = e.target.value
                    setSearchProduct(value)
                    setIsEditingProduct(true)
                    if (!value || value.trim() === '') {
                      setSelectedProduct(null)
                      setSelectedScript(null)
                      setScriptOptions([])
                    }
                  }}
                  onFocus={() => {
                    if (!hasAbatedItems) {
                      setIsEditingProduct(true)
                      setSearchProduct(selectedProduct?.name || '')
                    }
                  }}
                  placeholder={
                    hasAbatedItems
                      ? 'Reverta a OP antes de adicionar mais itens'
                      : 'Buscar produto...'
                  }
                  disabled={hasAbatedItems}
                />
                {isEditingProduct &&
                  !hasAbatedItems &&
                  productOptions.length > 0 && (
                    <DropdownList>
                      {productOptions.map((product) => (
                        <DropdownItem
                          key={product.id}
                          onClick={() => selectProduct(product)}
                        >
                          {product.name}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  )}
                {isEditingProduct &&
                  !hasAbatedItems &&
                  searchProduct &&
                  productOptions.length === 0 && (
                    <DropdownList>
                      <DropdownItem
                        style={{ pointerEvents: 'none', opacity: 0.6 }}
                      >
                        Nenhum produto encontrado
                      </DropdownItem>
                    </DropdownList>
                  )}
              </StyledFormGroup>

              <StyledFormGroup className='dropdown-container'>
                <label htmlFor='script'>Script</label>
                <input
                  type='text'
                  id='script'
                  value={isEditingScript ? '' : selectedScript?.name || ''}
                  onChange={() => {
                    if (selectedProduct && !hasAbatedItems)
                      setIsEditingScript(true)
                  }}
                  onFocus={() => {
                    if (selectedProduct && !hasAbatedItems)
                      setIsEditingScript(true)
                  }}
                  onClick={() => {
                    if (selectedProduct && !hasAbatedItems)
                      setIsEditingScript(true)
                  }}
                  onKeyDown={(e) => {
                    if (
                      (e.key === 'Backspace' || e.key === 'Delete') &&
                      selectedScript &&
                      !hasAbatedItems
                    ) {
                      e.preventDefault()
                      setSelectedScript(null)
                      setForm((prev) => ({
                        ...prev,
                        materials: prev.materials.filter((m) => m.isAbated),
                        resources: prev.resources.filter((r) => r.isAbated),
                        services: [],
                      }))
                    }
                  }}
                  placeholder={
                    hasAbatedItems
                      ? 'Reverta a OP antes de adicionar mais itens'
                      : selectedProduct
                      ? 'Selecione um script'
                      : 'Selecione um produto primeiro'
                  }
                  disabled={!selectedProduct || hasAbatedItems}
                />
                {isEditingScript &&
                  selectedProduct &&
                  !hasAbatedItems &&
                  scriptOptions.length > 0 && (
                    <DropdownList>
                      {scriptOptions.map((script) => (
                        <DropdownItem
                          key={script.id}
                          onClick={() => selectScript(script)}
                        >
                          {script.name}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  )}
                {isEditingScript &&
                  selectedProduct &&
                  !hasAbatedItems &&
                  scriptOptions.length === 0 && (
                    <DropdownList>
                      <DropdownItem
                        style={{ pointerEvents: 'none', opacity: 0.6 }}
                      >
                        Este produto não possui scripts
                      </DropdownItem>
                    </DropdownList>
                  )}
              </StyledFormGroup>
            </FormGrid>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Materiais</CardTitle>
            </StyledCardHeader>

            {form.materials.map((material, index) => (
              <MaterialItemContainer key={material.id}>
                <MaterialFields>
                  <StyledFormGroup className='dropdown-container'>
                    <label>
                      Material
                      {material.fromProduct &&
                        material.originalProductMaterialName && (
                          <span
                            style={{
                              fontWeight: 'normal',
                              fontSize: '0.9em',
                              color: '#666',
                            }}
                          >
                            {' '}
                            ({material.originalProductMaterialName})
                          </span>
                        )}
                    </label>
                    <input
                      type='text'
                      value={
                        editingMaterialIndex === index
                          ? searchMaterial
                          : material.material
                      }
                      onChange={(e) => {
                        setSearchMaterial(e.target.value)
                        setEditingMaterialIndex(index)
                      }}
                      onFocus={() => {
                        setEditingMaterialIndex(index)
                        if (
                          material.fromProduct &&
                          material.originalProductMaterialName
                        ) {
                          setFixedMaterialFilters((prev) => {
                            const { [index]: _, ...newFilters } = prev
                            return newFilters
                          })
                          setSearchMaterial(material.material)
                        } else {
                          setFixedMaterialFilters((prev) => {
                            const newFilters = { ...prev }
                            delete newFilters[index]
                            return newFilters
                          })
                          setSearchMaterial(material.material || '')
                        }
                      }}
                      disabled={material.isAbated}
                    />
                    {editingMaterialIndex === index &&
                      materialOptions.length > 0 && (
                        <DropdownList>
                          {materialOptions.map((option) => (
                            <DropdownItem
                              key={option.id}
                              onClick={() =>
                                selectItem('materials', index, option)
                              }
                            >
                              {option.name}
                            </DropdownItem>
                          ))}
                        </DropdownList>
                      )}
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Un. de medida</label>
                    <select
                      value={material.unitOfMeasure}
                      onChange={(e) =>
                        updateItem(
                          'materials',
                          material.id,
                          'unitOfMeasure',
                          e.target.value,
                        )
                      }
                      disabled={material.isAbated}
                    >
                      <option value='KG'>KG</option>
                      <option value='MM'>MM</option>
                    </select>
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Quantidade</label>
                    <input
                      type='number'
                      step='any'
                      min='1'
                      value={material.quantityToUse}
                      onChange={(e) =>
                        updateItem(
                          'materials',
                          material.id,
                          'quantityToUse',
                          e.target.value.replace(/[^\d.]/g, ''),
                        )
                      }
                      disabled={material.isAbated}
                      style={{
                        borderColor: material.hasInsufficientStock
                          ? 'red'
                          : undefined,
                        borderWidth: material.hasInsufficientStock
                          ? '2px'
                          : undefined,
                      }}
                    />
                  </StyledFormGroup>
                </MaterialFields>
                <InlineDeleteButton
                  type='button'
                  onClick={() => removeItem('materials', material.id)}
                  disabled={material.isAbated}
                >
                  <img src={trash} alt='Excluir' />
                </InlineDeleteButton>
              </MaterialItemContainer>
            ))}

            <SectionButtons>
              <AddButton
                type='button'
                onClick={addMaterial}
                disabled={!canAddMaterial}
              >
                <img src={plus} alt='Adicionar' />
                {canAddMaterial ? 'Adicionar material' : materialMessage}
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Insumos</CardTitle>
            </StyledCardHeader>

            {form.resources.map((resource, index) => (
              <MaterialItemContainer key={resource.id}>
                <MaterialFields>
                  <StyledFormGroup className='dropdown-container'>
                    <label>Insumo</label>
                    <input
                      type='text'
                      value={
                        editingResourceIndex === index
                          ? searchResource
                          : resource.resource
                      }
                      onChange={(e) => {
                        setSearchResource(e.target.value)
                        setEditingResourceIndex(index)
                      }}
                      onFocus={() => {
                        setEditingResourceIndex(index)
                        setSearchResource(resource.resource)
                      }}
                      disabled={resource.isAbated}
                    />
                    {editingResourceIndex === index &&
                      resourceOptions.length > 0 && (
                        <DropdownList>
                          {resourceOptions.map((option) => (
                            <DropdownItem
                              key={option.id}
                              onClick={() =>
                                selectItem('resources', index, option)
                              }
                            >
                              {option.name}
                            </DropdownItem>
                          ))}
                        </DropdownList>
                      )}
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Un. de medida</label>
                    <select
                      value={resource.unit}
                      onChange={(e) =>
                        updateItem(
                          'resources',
                          resource.id,
                          'unit',
                          e.target.value,
                        )
                      }
                      disabled={resource.isAbated}
                    >
                      <option value='KG'>KG</option>
                      <option value='G'>G</option>
                      <option value='UN'>UN</option>
                      <option value='MM'>MM</option>
                      <option value='CM'>CM</option>
                      <option value='M'>M</option>
                    </select>
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Quantidade</label>
                    <input
                      type='number'
                      min='0'
                      step={resource.unit === 'UN' ? '1' : '0.01'}
                      value={resource.quantity}
                      onChange={(e) => {
                        let value = e.target.value
                        if (resource.unit === 'UN' && value.includes('.'))
                          value = value.split('.')[0]
                        updateItem('resources', resource.id, 'quantity', value)
                      }}
                      disabled={resource.isAbated}
                    />
                  </StyledFormGroup>
                </MaterialFields>
                <InlineDeleteButton
                  type='button'
                  onClick={() => removeItem('resources', resource.id)}
                  disabled={resource.isAbated}
                >
                  <img src={trash} alt='Excluir' />
                </InlineDeleteButton>
              </MaterialItemContainer>
            ))}

            <SectionButtons>
              <AddButton
                type='button'
                onClick={addResource}
                disabled={!canAddResource}
              >
                <img src={plus} alt='Adicionar' />
                {canAddResource ? 'Adicionar insumo' : resourceMessage}
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Serviços</CardTitle>
            </StyledCardHeader>

            {form.services.map((service, index) => (
              <MaterialItemContainer key={service.id}>
                <MaterialFields>
                  <StyledFormGroup className='dropdown-container'>
                    <label>Serviço</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type='text'
                        value={
                          editingServiceIndex === index
                            ? searchService
                            : service.service
                        }
                        onChange={(e) => {
                          setSearchService(e.target.value)
                          setEditingServiceIndex(index)
                        }}
                        onFocus={() => {
                          setEditingServiceIndex(index)
                          setSearchService(service.service)
                        }}
                      />
                    </div>
                    {editingServiceIndex === index &&
                      serviceOptions.length > 0 && (
                        <DropdownList>
                          {serviceOptions.map((option) => (
                            <DropdownItem
                              key={option.id}
                              onClick={() =>
                                selectItem('services', index, option)
                              }
                            >
                              {option.name}
                            </DropdownItem>
                          ))}
                        </DropdownList>
                      )}
                  </StyledFormGroup>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <StyledFormGroup style={{ width: '100%' }}>
                      <label>Descrição</label>
                      <Textarea
                        value={service.description}
                        onChange={(e) =>
                          updateItem(
                            'services',
                            service.id,
                            'description',
                            e.target.value,
                          )
                        }
                      />
                    </StyledFormGroup>
                    <StyledFormGroup style={{ width: '100%' }}>
                      <label>Observação</label>
                      <Textarea
                        value={service.observation}
                        onChange={(e) =>
                          updateItem(
                            'services',
                            service.id,
                            'observation',
                            e.target.value,
                          )
                        }
                      />
                    </StyledFormGroup>
                  </div>
                </MaterialFields>
                <ServiceActionsContainer>
                  <OrderButtonsContainer>
                    <OrderButton
                      type='button'
                      onClick={() => reorderService(index, 'up')}
                      disabled={index === 0}
                      title='Mover para cima'
                    >
                      <ChevronUp />
                    </OrderButton>
                    <OrderButton
                      type='button'
                      onClick={() => reorderService(index, 'down')}
                      disabled={index === form.services.length - 1}
                      title='Mover para baixo'
                    >
                      <ChevronDown />
                    </OrderButton>
                  </OrderButtonsContainer>
                  <InlineDeleteButton
                    type='button'
                    onClick={() => removeItem('services', service.id)}
                    title='Excluir'
                  >
                    <img src={trash} alt='Excluir' />
                  </InlineDeleteButton>
                </ServiceActionsContainer>
              </MaterialItemContainer>
            ))}

            <SectionButtons>
              <AddButton type='button' onClick={addService}>
                <img src={plus} alt='Adicionar' />
                Adicionar serviço
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <ActionButtons
            style={{ marginTop: '24px', justifyContent: 'flex-end' }}
          >
            <CancelButton type='button' onClick={handleCancel}>
              Cancelar
            </CancelButton>
            <SaveButton
              type='button'
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              <img src={save} alt='Salvar' />
              {isSubmitting
                ? isEditMode
                  ? 'Atualizando...'
                  : 'Criando OP...'
                : isEditMode
                ? 'Atualizar OP'
                : 'Salvar OP'}
            </SaveButton>
          </ActionButtons>
        </form>
      </MainContainer>
    </>
  )
}

export default CreateOP
